/**
 * DriftAuditExportModal — downloads a tenant's drift audit history as CSV or
 * HTML, optionally narrowed by date range and event type. Built on the Carbon
 * modal (components/carbon/Modal): the format as selectable tiles, a date
 * range, the event types as selectable tags, and the export as the primary
 * action. The dialog can't be dismissed while the download is in flight.
 */

import { useState } from 'react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { DateInput } from '@/components/carbon/DateInput'
import { Modal } from '@/components/carbon/Modal'
import { RadioGroup } from '@/components/carbon/RadioGroup'
import { Tag } from '@/components/carbon/Tag'

interface DriftAuditExportModalProps {
  isOpen: boolean
  onClose: () => void
  tenantId: string
  tenantName?: string
}

const EVENT_TYPES = [
  { value: 'Detected', label: 'Detected', description: 'Guardian first saw the drift' },
  { value: 'Approved', label: 'Approved', description: 'User approved drift' },
  { value: 'Rejected', label: 'Rejected', description: 'User rejected drift' },
  { value: 'Remediated', label: 'Remediated', description: 'Drift was fixed' },
  { value: 'Redetected', label: 'Redetected', description: 'Drift reappeared' },
  { value: 'AutoApproved', label: 'Auto-approved', description: 'System auto-approved' },
]

const FORMATS = [
  { value: 'csv', label: 'CSV', helper: 'For Excel and data analysis' },
  { value: 'html', label: 'HTML', helper: 'A styled report for printing' },
]

export default function DriftAuditExportModal({
  isOpen,
  onClose,
  tenantId,
  tenantName
}: DriftAuditExportModalProps) {
  const [format, setFormat] = useState<'csv' | 'html'>('csv')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([])
  const [isExporting, setIsExporting] = useState(false)

  const handleEventTypeToggle = (eventType: string) => {
    setSelectedEventTypes(prev =>
      prev.includes(eventType)
        ? prev.filter(t => t !== eventType)
        : [...prev, eventType]
    )
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const blob = await api.downloadDriftAuditReport(
        tenantId,
        format,
        startDate || undefined,
        endDate || undefined,
        selectedEventTypes.length > 0 ? selectedEventTypes : undefined
      )

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().split('T')[0]
      link.download = `drift-audit-${tenantName || tenantId}-${timestamp}.${format}`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Drift audit report downloaded.')
      onClose()
    } catch (error: any) {
      console.error('Export failed:', error)

      // Try to parse error message from blob if it's JSON
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          toast.error(errorData.message || 'Failed to export audit report')
        } catch {
          toast.error('Failed to export audit report')
        }
      } else {
        toast.error(error?.response?.data?.message || error?.message || 'Failed to export audit report')
      }
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      title="Export drift audit history"
      subtitle="Every drift's timeline: who accepted or rejected it, when, their role and their comments."
      primaryAction={{
        label: isExporting ? 'Exporting' : 'Export',
        onClick: () => void handleExport(),
        loading: isExporting,
      }}
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: isExporting }}
    >
      <div className="flex flex-col gap-6">
        <RadioGroup
          name="drift-audit-format"
          legend="Export format"
          variant="tile"
          value={format}
          onChange={(v) => setFormat(v as 'csv' | 'html')}
          options={FORMATS}
          disabled={isExporting}
        />

        <fieldset className="min-w-0" disabled={isExporting}>
          <legend className="mb-2 text-helper text-text-secondary">Date range (optional)</legend>
          <div className="grid grid-cols-2 gap-4">
            <DateInput label="From" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} />
            <DateInput label="To" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </fieldset>

        <fieldset className="min-w-0" disabled={isExporting}>
          <legend className="mb-2 text-helper text-text-secondary">Event types (optional, all when none are selected)</legend>
          <div className="flex flex-wrap gap-2">
            {EVENT_TYPES.map((eventType) => (
              <Tag
                key={eventType.value}
                selected={selectedEventTypes.includes(eventType.value)}
                onClick={() => handleEventTypeToggle(eventType.value)}
                title={eventType.description}
              >
                {eventType.label}
              </Tag>
            ))}
          </div>
        </fieldset>

        <p className="m-0 text-helper text-text-helper">
          The audit history supports compliance documentation and accountability tracking.
        </p>
      </div>
    </Modal>
  )
}
